import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import { useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ISalesByChannels } from "store/managementStore";
import { updateSale } from "store/managementStore";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

type TForm = {
    note: string;
};

type TProps = {
    sale: ISalesByChannels;
    onClose: () => void;
};

const EditSaleNoteModal = ({ sale, onClose }: TProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { resolveLabel } = useCalculationBaseColumnLabels();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<TForm>({
        defaultValues: {
            note: sale.note ?? "",
        },
    });

    useEffect(() => {
        reset({ note: sale.note ?? "" });
    }, [sale, reset]);

    const onSubmit: SubmitHandler<TForm> = async (formData) => {
        const trimmedNote = formData?.note?.trim();
        const response = await dispatch(
            updateSale({
                ...(sale.titleId != null ? { titleId: sale.titleId } : {}),
                salesId: sale.id,
                channelId: sale.channelId,
                note: trimmedNote === "" ? null : trimmedNote,
            }),
        );

        if (response.meta.requestStatus === "fulfilled") {
            onClose();
        }
    };

    return (
        <form className={styles["edit-sale-note-modal"]} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles["sale-info"]}>
                <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>{t("Management.SaleDetails.Channel")}</span>
                    <span>{sale.name}</span>
                </div>
                <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>{t("Management.SaleDetails.Source")}</span>
                    <span>{sale.source ?? "-"}</span>
                </div>
                <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>{t("Management.SaleDate")}</span>
                    <span>
                        {formatLocaleDateString({ date: sale.date, options: { numeric: true, timeZone: "UTC" } })}
                    </span>
                </div>
                <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>{resolveLabel("turnover")}</span>
                    {/* A sale carrying no turnover of its own shows the revenue the API computed from its copies. */}
                    <span>
                        {sale.turnover
                            ? formatNumber(sale.turnover / 100, { isCurrency: true })
                            : formatNumber(sale.computedRevenue / 100, {
                                  isCurrency: true,
                                  preferredCurrency: sale.overridenPriceCurrency,
                              })}
                    </span>
                </div>
            </div>

            <TextFormComponent
                control={control}
                controlName="note"
                label={t("Management.SaleDetails.Note")}
                placeholder={t("EditSaleNote.NotePlaceholder")}
                fullWidth
            />

            <div className={styles["actions"]}>
                <StyledButton buttonType="secondary" onClick={onClose} type="button" fullWidth>
                    {t("Common.Close")}
                </StyledButton>
                <StyledButton buttonType="primary" type="submit" disabled={isSubmitting} fullWidth>
                    {t("Common.Save")}
                </StyledButton>
            </div>
        </form>
    );
};

export default EditSaleNoteModal;
